export type LoomControlSurfaceActuationMode = "independent" | "symmetric" | "antisymmetric";
export type LoomControlSurfaceSide = "port" | "starboard";
export type LoomSha256Revision = `sha256:${string}`;
export const LOOM_CONTROL_SURFACE_EVALUATOR_ID = "loom.native-wing-control-surfaces/v1" as const;
export const LOOM_CONTROL_SURFACE_TOPOLOGY_ID = "separate-fixed-and-mirrored-movable-bodies/v1" as const;
export const LOOM_CONTROL_SURFACE_KINEMATICS_ID = "straight-3d-hinge-rigid-semantic-te-down/v1" as const;
const MAX_NEUTRAL_WING_DEFINITION_BYTES = 512 * 1024;
const MAX_CONTROL_SURFACE_SERIALIZED_BYTES = 128 * 1024;

export interface LoomWingControlSurfaceDefinition {
  readonly id: string;
  readonly label: string;
  readonly spanBand: { readonly start: number; readonly end: number };
  readonly hinge: {
    readonly inboard: { readonly xOverChord: number; readonly skinHeightFraction: number };
    readonly outboard: { readonly xOverChord: number; readonly skinHeightFraction: number };
  };
  readonly noseCove: {
    readonly kind: "sealed" | "positive-gap";
    readonly gapRatio: number;
    readonly filletRatio: number;
    readonly endGapRatio: number;
  };
  readonly limitsDeg: {
    readonly port: { readonly minimum: number; readonly maximum: number };
    readonly starboard: { readonly minimum: number; readonly maximum: number };
  };
}

export interface LoomWingControlSurfaceCollection {
  readonly schema: "loom.wing-control-surface-collection/v1";
  readonly wingId: string;
  readonly wingDefinitionRevision: LoomSha256Revision;
  readonly controlSurfaces: readonly LoomWingControlSurfaceDefinition[];
}

export interface LoomWingControlSurfacePoseState {
  readonly controlSurfaceId: string;
  readonly mode: LoomControlSurfaceActuationMode;
  readonly portDeflectionDeg: number;
  readonly starboardDeflectionDeg: number;
}

export interface LoomWingControlSurfacePose {
  readonly schema: "loom.wing-control-surface-pose/v1";
  readonly definitionRevision: LoomSha256Revision;
  readonly selectedControlSurfaceId: string | null;
  readonly states: readonly LoomWingControlSurfacePoseState[];
}

type RecordValue = Record<string, unknown>;

function record(value: unknown, path: string): RecordValue {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as RecordValue;
}

function exact(value: RecordValue, keys: readonly string[], path: string): void {
  const extras = Object.keys(value).filter((key) => !keys.includes(key));
  const missing = keys.filter((key) => !(key in value));
  if (extras.length) throw new Error(`${path} contains unknown fields: ${extras.join(", ")}`);
  if (missing.length) throw new Error(`${path} is missing fields: ${missing.join(", ")}`);
}

function finite(value: unknown, path: string, minimum: number, maximum: number): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < minimum || value > maximum) {
    throw new Error(`${path} must be a finite number in [${minimum}, ${maximum}]`);
  }
  return value;
}

function token(value: unknown, path: string): string {
  if (typeof value !== "string" || !/^[A-Za-z0-9][A-Za-z0-9._-]{0,159}$/.test(value)) {
    throw new Error(`${path} must be a stable Loom id`);
  }
  return value;
}

function hasUnpairedSurrogate(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const unit = value.charCodeAt(index);
    if (unit >= 0xd800 && unit <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) return true;
      index += 1;
    } else if (unit >= 0xdc00 && unit <= 0xdfff) return true;
  }
  return false;
}

function label(value: unknown, path: string): string {
  if (typeof value !== "string" || value === "" || [...value].length > 160
    || value !== value.trim() || hasUnpairedSurrogate(value)
    || [...value].some((character) => character.charCodeAt(0) < 32)) {
    throw new Error(`${path} must be a bounded display label without surrounding whitespace or control characters`);
  }
  return value;
}

function revision(value: unknown, path: string): LoomSha256Revision {
  if (typeof value !== "string" || !/^sha256:[0-9a-f]{64}$/.test(value)) {
    throw new Error(`${path} must be a lowercase SHA-256 revision`);
  }
  return value as LoomSha256Revision;
}

function literal<T extends string>(value: unknown, allowed: readonly T[], path: string): T {
  if (typeof value !== "string" || !allowed.includes(value as T)) {
    throw new Error(`${path} is unsupported`);
  }
  return value as T;
}

function immutable<T>(value: T): T {
  if (typeof value !== "object" || value === null || Object.isFrozen(value)) return value;
  Object.values(value as RecordValue).forEach(immutable);
  return Object.freeze(value);
}

function parseHingePoint(value: unknown, path: string) {
  const item = record(value, path);
  exact(item, ["xOverChord", "skinHeightFraction"], path);
  return {
    xOverChord: finite(item.xOverChord, `${path}.xOverChord`, 0.5, 0.98),
    skinHeightFraction: finite(item.skinHeightFraction, `${path}.skinHeightFraction`, 0, 1),
  };
}

function parseLimits(value: unknown, path: string) {
  const item = record(value, path);
  exact(item, ["minimum", "maximum"], path);
  const minimum = finite(item.minimum, `${path}.minimum`, -90, 90);
  const maximum = finite(item.maximum, `${path}.maximum`, -90, 90);
  if (minimum >= maximum || minimum > 0 || maximum < 0) {
    throw new Error(`${path} must be ordered and include neutral zero`);
  }
  return { minimum, maximum };
}

function parseControlSurface(value: unknown, path: string): LoomWingControlSurfaceDefinition {
  const item = record(value, path);
  exact(item, ["id", "label", "spanBand", "hinge", "noseCove", "limitsDeg"], path);
  const spanBand = record(item.spanBand, `${path}.spanBand`);
  exact(spanBand, ["start", "end"], `${path}.spanBand`);
  const start = finite(spanBand.start, `${path}.spanBand.start`, 0, 1);
  const end = finite(spanBand.end, `${path}.spanBand.end`, 0, 1);
  if (end - start < 1e-4) throw new Error(`${path}.spanBand width must be at least 1e-4`);
  const hinge = record(item.hinge, `${path}.hinge`);
  exact(hinge, ["inboard", "outboard"], `${path}.hinge`);
  const noseCove = record(item.noseCove, `${path}.noseCove`);
  exact(noseCove, ["kind", "gapRatio", "filletRatio", "endGapRatio"], `${path}.noseCove`);
  const kind = literal(noseCove.kind, ["sealed", "positive-gap"] as const, `${path}.noseCove.kind`);
  const gapRatio = finite(noseCove.gapRatio, `${path}.noseCove.gapRatio`, 0, 0.05);
  if ((kind === "sealed" && gapRatio !== 0) || (kind === "positive-gap" && gapRatio <= 0)) {
    throw new Error(`${path}.noseCove.gapRatio is inconsistent with ${kind}`);
  }
  const limitsDeg = record(item.limitsDeg, `${path}.limitsDeg`);
  exact(limitsDeg, ["port", "starboard"], `${path}.limitsDeg`);
  return {
    id: token(item.id, `${path}.id`), label: label(item.label, `${path}.label`),
    spanBand: { start, end },
    hinge: {
      inboard: parseHingePoint(hinge.inboard, `${path}.hinge.inboard`),
      outboard: parseHingePoint(hinge.outboard, `${path}.hinge.outboard`),
    },
    noseCove: {
      kind, gapRatio,
      filletRatio: finite(noseCove.filletRatio, `${path}.noseCove.filletRatio`, 0, 0.05),
      endGapRatio: finite(noseCove.endGapRatio, `${path}.noseCove.endGapRatio`, 0, 0.05),
    },
    limitsDeg: {
      port: parseLimits(limitsDeg.port, `${path}.limitsDeg.port`),
      starboard: parseLimits(limitsDeg.starboard, `${path}.limitsDeg.starboard`),
    },
  };
}

export function parseLoomWingControlSurfaceCollection(value: unknown): LoomWingControlSurfaceCollection {
  if (canonicalBytes(value).byteLength > MAX_CONTROL_SURFACE_SERIALIZED_BYTES) {
    throw new Error("controlSurfaceCollection exceeds its 128 KiB byte budget");
  }
  const item = record(value, "controlSurfaceCollection");
  exact(item, ["schema", "wingId", "wingDefinitionRevision", "controlSurfaces"], "controlSurfaceCollection");
  if (item.schema !== "loom.wing-control-surface-collection/v1") {
    throw new Error("controlSurfaceCollection.schema must be loom.wing-control-surface-collection/v1");
  }
  if (!Array.isArray(item.controlSurfaces)) throw new Error("controlSurfaceCollection.controlSurfaces must be an array");
  if (item.controlSurfaces.length > 16) throw new Error("controlSurfaceCollection supports at most 16 controls");
  const controls = item.controlSurfaces.map((entry, index) => parseControlSurface(
    entry, `controlSurfaceCollection.controlSurfaces[${index}]`,
  )).sort((a, b) => a.spanBand.start - b.spanBand.start || a.spanBand.end - b.spanBand.end
    || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  if (new Set(controls.map(({ id }) => id)).size !== controls.length) {
    throw new Error("controlSurfaceCollection.controlSurfaces contains duplicate ids");
  }
  for (let index = 1; index < controls.length; index += 1) {
    if (controls[index]!.spanBand.start - controls[index - 1]!.spanBand.end <= 1e-9) {
      throw new Error("controlSurfaceCollection span bands overlap or have ambiguous adjacency");
    }
  }
  return immutable({
    schema: "loom.wing-control-surface-collection/v1",
    wingId: token(item.wingId, "controlSurfaceCollection.wingId"),
    wingDefinitionRevision: revision(item.wingDefinitionRevision, "controlSurfaceCollection.wingDefinitionRevision"),
    controlSurfaces: controls,
  });
}

export function createDefaultLoomWingControlSurface(
  id: string,
  name = "Control surface",
  spanBand: { start: number; end: number } = { start: 0.55, end: 0.9 },
): LoomWingControlSurfaceDefinition {
  return parseControlSurface({
    id, label: name, spanBand,
    hinge: {
      inboard: { xOverChord: 0.72, skinHeightFraction: 0.5 },
      outboard: { xOverChord: 0.72, skinHeightFraction: 0.5 },
    },
    noseCove: { kind: "sealed", gapRatio: 0, filletRatio: 0.01, endGapRatio: 0.005 },
    limitsDeg: {
      port: { minimum: -30, maximum: 30 },
      starboard: { minimum: -30, maximum: 30 },
    },
  }, "controlSurface");
}

function stateFor(value: unknown, path: string, definition: LoomWingControlSurfaceDefinition) {
  const item = record(value, path);
  exact(item, ["controlSurfaceId", "mode", "portDeflectionDeg", "starboardDeflectionDeg"], path);
  const controlSurfaceId = token(item.controlSurfaceId, `${path}.controlSurfaceId`);
  if (controlSurfaceId !== definition.id) throw new Error(`${path}.controlSurfaceId is stale or out of order`);
  const mode = literal(item.mode, ["independent", "symmetric", "antisymmetric"] as const, `${path}.mode`);
  const portDeflectionDeg = finite(item.portDeflectionDeg, `${path}.port deflection (instance limits)`, definition.limitsDeg.port.minimum, definition.limitsDeg.port.maximum);
  const starboardDeflectionDeg = finite(item.starboardDeflectionDeg, `${path}.starboard deflection (instance limits)`, definition.limitsDeg.starboard.minimum, definition.limitsDeg.starboard.maximum);
  if (mode === "symmetric" && portDeflectionDeg !== starboardDeflectionDeg) throw new Error(`${path} symmetric sides must match exactly`);
  if (mode === "antisymmetric" && portDeflectionDeg !== -starboardDeflectionDeg) throw new Error(`${path} antisymmetric sides must be exact opposites`);
  return { controlSurfaceId, mode, portDeflectionDeg, starboardDeflectionDeg };
}

export function parseLoomWingControlSurfacePose(
  value: unknown,
  collection: LoomWingControlSurfaceCollection,
  expectedDefinitionRevision: LoomSha256Revision,
): LoomWingControlSurfacePose {
  if (canonicalBytes(value).byteLength > MAX_CONTROL_SURFACE_SERIALIZED_BYTES) {
    throw new Error("controlSurfacePose exceeds its 128 KiB byte budget");
  }
  const item = record(value, "controlSurfacePose");
  exact(item, ["schema", "definitionRevision", "selectedControlSurfaceId", "states"], "controlSurfacePose");
  if (item.schema !== "loom.wing-control-surface-pose/v1") throw new Error("controlSurfacePose.schema is unsupported");
  const definitionRevision = revision(item.definitionRevision, "controlSurfacePose.definitionRevision");
  if (definitionRevision !== expectedDefinitionRevision) throw new Error("controlSurfacePose definition binding is stale");
  if (!Array.isArray(item.states) || item.states.length !== collection.controlSurfaces.length) {
    throw new Error("controlSurfacePose.states must cover every control exactly once");
  }
  const rawStates = item.states.map((state, index) => record(state, `controlSurfacePose.states[${index}]`));
  const statesById = new Map(rawStates.map((state, index) => [
    token(state.controlSurfaceId, `controlSurfacePose.states[${index}].controlSurfaceId`), state,
  ]));
  if (statesById.size !== rawStates.length) throw new Error("controlSurfacePose.states contains duplicate ids");
  const states = collection.controlSurfaces.map((definition, index) => {
    const state = statesById.get(definition.id);
    if (!state) throw new Error("controlSurfacePose.states must cover every control exactly once");
    return stateFor(state, `controlSurfacePose.states[${index}]`, definition);
  });
  const selectedControlSurfaceId = item.selectedControlSurfaceId === null
    ? null : token(item.selectedControlSurfaceId, "controlSurfacePose.selectedControlSurfaceId");
  if (selectedControlSurfaceId !== null && !states.some(({ controlSurfaceId }) => controlSurfaceId === selectedControlSurfaceId)) {
    throw new Error("controlSurfacePose.selectedControlSurfaceId is stale");
  }
  return immutable({ schema: "loom.wing-control-surface-pose/v1", definitionRevision, selectedControlSurfaceId, states });
}

export function createLoomWingControlSurfacePose(
  collection: LoomWingControlSurfaceCollection,
  definitionRevision: LoomSha256Revision,
  selectedControlSurfaceId: string | null = collection.controlSurfaces[0]?.id ?? null,
): LoomWingControlSurfacePose {
  return parseLoomWingControlSurfacePose({
    schema: "loom.wing-control-surface-pose/v1", definitionRevision, selectedControlSurfaceId,
    states: collection.controlSurfaces.map(({ id }) => ({
      controlSurfaceId: id, mode: "independent", portDeflectionDeg: 0, starboardDeflectionDeg: 0,
    })),
  }, collection, definitionRevision);
}

export function controlSurfaceIdForBodyId(
  collection: LoomWingControlSurfaceCollection,
  bodyId: string,
): string | null {
  return collection.controlSurfaces.find(({ id }) => (
    bodyId === `${collection.wingId}:control:${id}:port`
    || bodyId === `${collection.wingId}:control:${id}:starboard`
  ))?.id ?? null;
}

export function resolveLoomWingControlSurfaceCommand(
  definition: LoomWingControlSurfaceDefinition,
  mode: Exclude<LoomControlSurfaceActuationMode, "independent">,
  commandDeg: number,
) {
  const strictMode = literal(mode, ["symmetric", "antisymmetric"] as const, "mode");
  const starboardDeflectionDeg = finite(commandDeg, "commandDeg", definition.limitsDeg.starboard.minimum, definition.limitsDeg.starboard.maximum);
  const portDeflectionDeg = strictMode === "symmetric" ? starboardDeflectionDeg : -starboardDeflectionDeg;
  finite(portDeflectionDeg, "resolved port command", definition.limitsDeg.port.minimum, definition.limitsDeg.port.maximum);
  return { portDeflectionDeg, starboardDeflectionDeg };
}

export function updateLoomWingControlSurfacePose(
  collection: LoomWingControlSurfaceCollection,
  pose: LoomWingControlSurfacePose,
  controlSurfaceId: string,
  update: Partial<Omit<LoomWingControlSurfacePoseState, "controlSurfaceId">>,
): LoomWingControlSurfacePose {
  const definition = collection.controlSurfaces.find(({ id }) => id === controlSurfaceId);
  if (!definition) throw new Error(`control surface ${controlSurfaceId} does not exist`);
  const states = pose.states.map((state) => {
    if (state.controlSurfaceId !== controlSurfaceId) return state;
    const mode = update.mode ?? state.mode;
    if (mode === "independent") return { ...state, ...update, mode };
    const command = update.starboardDeflectionDeg ?? (mode === "symmetric" ? state.starboardDeflectionDeg : state.starboardDeflectionDeg);
    return { ...state, mode, ...resolveLoomWingControlSurfaceCommand(definition, mode, command) };
  });
  return parseLoomWingControlSurfacePose({ ...pose, states }, collection, pose.definitionRevision);
}

function canonicalJson(value: unknown): string {
  if (value === null || typeof value === "boolean") return JSON.stringify(value);
  if (typeof value === "string") {
    if (hasUnpairedSurrogate(value)) throw new Error("canonical JSON contains invalid Unicode");
    return JSON.stringify(value);
  }
  if (typeof value === "number" && Number.isFinite(value)) return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (typeof value === "object") {
    const item = value as RecordValue;
    return `{${Object.keys(item).sort().map((key) => `${canonicalJson(key)}:${canonicalJson(item[key])}`).join(",")}}`;
  }
  throw new Error("canonical JSON contains unsupported content");
}

function canonicalBytes(value: unknown): Uint8Array {
  return new TextEncoder().encode(canonicalJson(value));
}

async function sha256(value: unknown): Promise<LoomSha256Revision> {
  if (!globalThis.crypto?.subtle) throw new Error("WebCrypto SHA-256 is required for Loom control-surface receipts");
  const digest = await crypto.subtle.digest("SHA-256", canonicalBytes(value));
  return `sha256:${[...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

export function deriveLoomWingDefinitionRevision(wingId: string, neutralWingDefinition: unknown) {
  const strictWingId = token(wingId, "wingId");
  record(neutralWingDefinition, "neutralWingDefinition");
  const envelope = {
    schema: "loom.wing-control-surface-wing-definition-revision-input/v1",
    wingId: strictWingId,
    neutralWingDefinition,
  };
  if (canonicalBytes(envelope).byteLength > MAX_NEUTRAL_WING_DEFINITION_BYTES) {
    throw new Error("neutralWingDefinition exceeds its 512 KiB byte budget");
  }
  return sha256(envelope);
}

export function deriveLoomWingControlSurfaceDefinitionRevision(collection: LoomWingControlSurfaceCollection) {
  const definition = parseLoomWingControlSurfaceCollection(collection);
  return sha256({ definition, evaluatorId: LOOM_CONTROL_SURFACE_EVALUATOR_ID, topology: LOOM_CONTROL_SURFACE_TOPOLOGY_ID });
}

export async function deriveLoomWingControlSurfacePoseRevision(
  collection: LoomWingControlSurfaceCollection,
  pose: LoomWingControlSurfacePose,
) {
  const strictCollection = parseLoomWingControlSurfaceCollection(collection);
  const currentDefinitionRevision = await deriveLoomWingControlSurfaceDefinitionRevision(strictCollection);
  const strictPose = parseLoomWingControlSurfacePose(pose, strictCollection, currentDefinitionRevision);
  return sha256({ kinematicsId: LOOM_CONTROL_SURFACE_KINEMATICS_ID, pose: strictPose });
}

export function deriveNoncanonicalLoomStoredPoseMigrationRevision(
  collection: LoomWingControlSurfaceCollection,
  storedDefinitionRevision: LoomSha256Revision,
  pose: LoomWingControlSurfacePose,
) {
  const strictCollection = parseLoomWingControlSurfaceCollection(collection);
  const strictRevision = revision(storedDefinitionRevision, "storedDefinitionRevision");
  const strictPose = parseLoomWingControlSurfacePose(pose, strictCollection, strictRevision);
  return sha256({ kinematicsId: LOOM_CONTROL_SURFACE_KINEMATICS_ID, pose: strictPose });
}
